import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { X } from 'lucide-react';

const CONSENT_KEY = 'ga_consent';

interface GASettings {
  enabled: boolean;
  id: string;
}

export function GoogleAnalytics() {
  const [consent, setConsent] = useState<'accepted' | 'declined' | null>(null);
  const [showBanner, setShowBanner] = useState(false);

  // Load GA settings from database
  const { data: settings } = useQuery({
    queryKey: ['ga-settings'],
    queryFn: async (): Promise<GASettings> => {
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value')
        .in('key', ['google_analytics_enabled', 'google_analytics_id']);

      if (error) throw error;

      const enabled = data?.find((s) => s.key === 'google_analytics_enabled');
      const id = data?.find((s) => s.key === 'google_analytics_id');

      return {
        enabled: enabled?.value === true || enabled?.value === 'true',
        id: typeof id?.value === 'string' ? id.value.replace(/"/g, '') : '',
      };
    },
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  // Check stored consent
  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored === 'accepted' || stored === 'declined') {
      setConsent(stored);
    } else if (settings?.enabled && settings?.id) {
      // Only show banner if GA is enabled
      setShowBanner(true);
    }
  }, [settings]);

  // Load GA script when consent is given
  useEffect(() => {
    if (consent === 'accepted' && settings?.enabled && settings?.id) {
      loadGoogleAnalytics(settings.id);
    }
  }, [consent, settings]);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    setConsent('accepted');
    setShowBanner(false);
  };

  const handleDecline = () => {
    localStorage.setItem(CONSENT_KEY, 'declined');
    setConsent('declined');
    setShowBanner(false);
  };

  // Don't render anything if GA is not enabled
  if (!settings?.enabled || !settings?.id) {
    return null;
  }

  // Don't show banner if consent already given
  if (!showBanner) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t border-gray-200 shadow-lg safe-area-pb">
      <div className="max-w-lg mx-auto">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 text-sm">Cookie-Einstellungen</h3>
            <p className="text-xs text-gray-600 mt-1">
              Wir verwenden Cookies zur Analyse der Nutzung unserer App. Du kannst selbst entscheiden,
              ob du diese erlauben möchtest.
            </p>
          </div>
          <button
            onClick={handleDecline}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label="Schließen"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex gap-2 mt-3">
          <button
            onClick={handleDecline}
            className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Ablehnen
          </button>
          <button
            onClick={handleAccept}
            className="flex-1 px-3 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors"
          >
            Akzeptieren
          </button>
        </div>
      </div>
    </div>
  );
}

function loadGoogleAnalytics(measurementId: string) {
  // Check if already loaded
  if (document.querySelector(`script[src*="googletagmanager.com/gtag"]`)) {
    return;
  }

  // Load gtag.js
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script.async = true;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  }
  gtag('js', new Date());
  gtag('config', measurementId, {
    anonymize_ip: true, // GDPR compliance
    cookie_flags: 'SameSite=None;Secure',
  });

  // Make gtag available globally
  (window as unknown as { gtag: typeof gtag }).gtag = gtag;
}

// Extend Window interface
declare global {
  interface Window {
    dataLayer: unknown[];
  }
}
